import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, ArrowLeft } from "lucide-react";
import logo from "@/assets/logo.png";

export default function Login() {
  const { user, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    if (isSignUp) {
      const { error } = await signUp(email, password, fullName);
      if (error) setError(error.message);
      else setMessage("Check your email to confirm your account.");
    } else {
      const { error } = await signIn(email, password);
      if (error) {
        if (error.message === "Email not confirmed") {
          setError("Email not confirmed. Check your email for confirmation.");
        } else {
          setError(error.message);
        }
      } else {
        navigate("/", { replace: true });
      }
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) setError(error.message);
    else setMessage("Check your email for a password reset link.");
    setLoading(false);
  };

  if (showForgot) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-b from-primary/5 to-background">
        <Helmet>
          <title>Reset Password — DMA Data Capture Hub</title>
          <meta name="description" content="Reset your DMA Data Capture Hub password." />
          <link rel="canonical" href="https://dma-data-capture-gabcle.lovable.app/login" />
        </Helmet>
        <main className="w-full max-w-sm">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <img src={logo} alt="Gabcle DevHub" className="h-8 w-8" />
            </div>
            <CardTitle className="text-xl">Reset Password</CardTitle>
            <h1 className="sr-only">Reset your DMA Data Capture Hub password</h1>
            <CardDescription>Enter your email to receive a reset link</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12"
                  required
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              {message && <p className="text-sm text-success">{message}</p>}
              <Button type="submit" disabled={loading} className="w-full h-12 text-base">
                {loading && <Loader2 className="h-5 w-5 animate-spin mr-2" />}
                Send Reset Link
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                <button
                  type="button"
                  onClick={() => { setShowForgot(false); setError(null); setMessage(null); }}
                  className="text-primary font-medium hover:underline"
                >
                  Back to Sign In
                </button>
              </p>
            </form>
          </CardContent>
        </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-b from-primary/5 to-background">
      <Helmet>
        <title>Sign in to DMA Data Capture Hub</title>
        <meta name="description" content="Sign in to the DMA Data Capture Hub by International Aramoon Co. Ltd." />
        <link rel="canonical" href="https://dma-data-capture-gabcle.lovable.app/login" />
      </Helmet>
      <main className="w-full max-w-sm">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <img src={logo} alt="Gabcle DevHub" className="h-8 w-8" />
          </div>
          <CardTitle className="text-xl">DMA Survey App</CardTitle>
          <h1 className="sr-only">Sign in to DMA Survey</h1>
          <CardDescription>
            {isSignUp ? "Create your account" : "Sign in to continue"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="h-12"
                  required
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {!isSignUp && (
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-xs text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
                minLength={6}
                required
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
            {message && <p className="text-sm text-success">{message}</p>}

            <Button type="submit" disabled={loading} className="w-full h-12 text-base">
              {loading && <Loader2 className="h-5 w-5 animate-spin mr-2" />}
              {isSignUp ? "Sign Up" : "Sign In"}
            </Button>

          </form>
        </CardContent>
        <div className="px-6 pb-6 text-center">
          <Button variant="ghost" size="sm" asChild className="text-muted-foreground">
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </Button>
        </div>
      </Card>
      </main>
    </div>
  );
}
